
import {
  IngestDocument, IngestTextFragment, IngestFragmentBox,
} from '@/model';
import { groupBy, notEmpty } from '@/util/array-util';
import { traverse } from '@/util/functional-iterator';
import { NodeSelection, removeInterval, merge } from '@/util/text-selection';
import Vue from 'vue';

export default Vue.extend({
  name: 'FragmentSelectionToolbox',

  props: {
    selection: { type: Object as () => NodeSelection | undefined, required: false },
    ingest: { type: Object as () => IngestDocument | undefined, required: false },
    edit: { type: Object as () => IngestTextFragment | undefined },
  },

  data() {
    return {
      offset: {
        left: 0,
        top: 0,
      },
    };
  },

  mounted() {
    const element = this.$el as HTMLElement;
    [this.offset.left, this.offset.top] = traverse(element,
      (el) => el?.offsetParent as HTMLElement || el.parentElement?.offsetParent || undefined)
      .collect()
      .reduce((acc, p) => [acc[0] + p.offsetLeft, acc[1] + p.offsetTop], [0, 0]);
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  computed: {
    show(): boolean {
      return !!this.selection;
    },

    positionStyle(): { top: string; left: string } | undefined {
      const nodes = this.selection?.nodes;
      const end = nodes ? nodes[nodes.length - 1]?.element : undefined;
      const rect = end?.getBoundingClientRect();
      return rect && {
        left: `${rect.left + rect.width + window.pageXOffset - this.offset.left}px`,
        top: `${rect.top + window.pageYOffset - this.offset.top}px`,
      };
    },

    boxes() {
      const seqNo = Math.max(...this.edit?.boxes.map((b) => b.seqNo)
        .filter(notEmpty) || [-1]);
      const nodes = (this.selection?.nodes || [])
        .map((s) => {
          const pi = parseInt(s.context.pageIndex as string, 10);
          const bi = parseInt(s.context.boxIndex as string, 10);
          const ti = parseInt(s.context.textIndex as string, 10);
          return {
            pi, bi, ti, s,
          };
        });
      return Array.from(groupBy(nodes, (n) => `${n.pi}/${n.bi}`))
        .map(([, ns], i) => {
          const box = this.ingest?.pages[ns[0].pi]?.boxes[ns[0].bi];
          const start = ns.find((n) => n.s.startOffset !== undefined);
          const end = ns.find((n) => n.s.endOffset !== undefined);
          const b = box && {
            boxId: box.id,
            from: start?.s.startOffset !== undefined ? start.ti + start.s.startOffset : 0,
            to: end?.s.endOffset !== undefined ? end.ti + end.s.endOffset : box.text?.length || 0,
            seqNo: seqNo + i + 1,
          };
          return b;
        })
        .filter(notEmpty);
    },
  },

  methods: {
    emitFragment(deselect: boolean) {
      this.$emit('selection', this.getFragment(deselect));
    },

    handleKeydown(event: KeyboardEvent) {
      if (!this.show || event.ctrlKey || this.isEditableTarget(event.target)) {
        return;
      }
      if (event.key === 'a' || event.key === 'A') {
        this.emitFragment(false);
        event.preventDefault();
      } else if (this.edit && (event.key === 'd' || event.key === 'D')) {
        this.emitFragment(true);
        event.preventDefault();
      }
    },
    isEditableTarget(target: EventTarget | null): boolean {
      const el = target as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    },

    removeInterval(intervals: IngestFragmentBox[], remove: IngestFragmentBox): IngestFragmentBox[] {
      return intervals
        .flatMap((interval) => {
          if (interval.boxId === remove.boxId) {
            return removeInterval(interval, remove)
              .map((i) => ({ boxId: interval.boxId, ...i, seqNo: interval.seqNo }));
          }
          return [{ ...interval }];
        });
    },
    removeIntervals(intervals: IngestFragmentBox[], removeIntervals: IngestFragmentBox[]) {
      return removeIntervals.reduce(this.removeInterval, intervals);
    },
    mergeBoxes(boxes: IngestFragmentBox[]) {
      return Array.from(groupBy(boxes, 'boxId')).flatMap(([_, f]) => merge<IngestFragmentBox>([...f]));
    },
    updateSeqNo(boxes: IngestFragmentBox[]): IngestFragmentBox[] {
      return Array.from(groupBy(boxes, (b) => b.boxId))
        .sort(([, a], [, b]) => this.compareIntervalSeq([...a], [...b]))
        .flatMap(([, group]) => [...group].sort((a, b) => a.from - b.from))
        .map((box, seqNo) => ({ ...box, seqNo }));
    },
    minSeqNo(boxes: IngestFragmentBox[]): number | undefined {
      const values = boxes.map((b) => b.seqNo);
      const anyUndefined = values.find((b) => !b); // find any undefined
      return !anyUndefined && values.length > 0 ? Math.min(...values.filter(notEmpty)) : undefined;
    },
    compareIntervalSeq(a: IngestFragmentBox[], b: IngestFragmentBox[]): number {
      const minA = this.minSeqNo(a);
      const minB = this.minSeqNo(b);
      if (!!minA && !!minB) {
        return minA - minB;
      } else if (!minA && !minB) {
        return a[0].boxId - b[0].boxId;
      }
      return !minA ? 1 : -1;
    },
    getFragment(deselect: boolean): IngestTextFragment {
      if (this.edit) {
        return {
          ...this.edit,
          boxes: this.updateSeqNo(
            this.mergeBoxes(deselect ? this.removeIntervals([...this.edit.boxes], this.boxes) : [...this.edit.boxes, ...this.boxes]),
          ),
        };
      }
      // New fragment
      return {
        id: 0,
        labels: [],
        boxes: this.boxes || [],
        type: 'text',
        isolate: false,
      };
    },
  },

});
